/**
 * Picture in / sticker out for the sticker maker — plain canvas, on-device.
 *  - In: decode with the EXIF orientation applied, at most 768 px on the long
 *    side (same as the phone's decodeForSticker), as straight-alpha RGBA.
 *  - Out: a 512×512 transparent PNG. Only if that PNG is over the My stickers
 *    budget (photo-heavy stickers can be) do we fall back to WebP (keeps alpha),
 *    then to a smaller PNG — so a sticker can always be kept and sent.
 */
import { MY_STICKER_MAX_BYTES, type MyStickerMediaType } from "@veto/storage";

export const MAX_SOURCE_SIDE = 768;
/** Refuse absurd inputs before decoding ("picture bombs"). */
const MAX_SOURCE_BYTES = 40 * 1024 * 1024;
const MAX_SOURCE_PIXELS = 80_000_000;

export const OPEN_FAILED_MESSAGE = "Couldn't open that picture.";

export interface Picture {
  width: number;
  height: number;
  rgba: Uint8ClampedArray;
}

function canvas2d(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error(OPEN_FAILED_MESSAGE);
  return { canvas, ctx };
}

async function decodeWithImg(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.decoding = "async";
  img.src = url;
  await img.decode();
  return img;
}

/** Decode a photo or sticker; EXIF orientation applied; long side ≤ `maxSide`. */
export async function loadPicture(blob: Blob, maxSide = MAX_SOURCE_SIDE): Promise<Picture> {
  if (blob.size === 0 || blob.size > MAX_SOURCE_BYTES) throw new Error(OPEN_FAILED_MESSAGE);
  let source: CanvasImageSource | null = null;
  let w0 = 0;
  let h0 = 0;
  let release = () => {};
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(blob, { imageOrientation: "from-image" });
      source = bmp;
      w0 = bmp.width;
      h0 = bmp.height;
      release = () => bmp.close();
    } catch {
      source = null;
    }
  }
  if (!source) {
    // <img> applies EXIF orientation by default (CSS image-orientation: from-image).
    const url = URL.createObjectURL(blob);
    try {
      const img = await decodeWithImg(url);
      source = img;
      w0 = img.naturalWidth;
      h0 = img.naturalHeight;
    } catch {
      URL.revokeObjectURL(url);
      throw new Error(OPEN_FAILED_MESSAGE);
    }
    release = () => URL.revokeObjectURL(url);
  }
  try {
    if (!w0 || !h0 || w0 * h0 > MAX_SOURCE_PIXELS) throw new Error(OPEN_FAILED_MESSAGE);
    const scale = Math.min(1, maxSide / Math.max(w0, h0));
    const width = Math.max(1, Math.round(w0 * scale));
    const height = Math.max(1, Math.round(h0 * scale));
    const { ctx } = canvas2d(width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, 0, 0, width, height);
    return { width, height, rgba: ctx.getImageData(0, 0, width, height).data };
  } finally {
    release();
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob(resolve, type, quality);
    } catch {
      resolve(null);
    }
  });
}

function scaledCopy(src: HTMLCanvasElement, longSide: number): HTMLCanvasElement {
  const k = longSide / Math.max(src.width, src.height);
  const { canvas, ctx } = canvas2d(Math.max(1, Math.round(src.width * k)), Math.max(1, Math.round(src.height * k)));
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** PNG first; WebP / smaller PNG only when the PNG would not fit My stickers. */
export async function encodeSticker(canvas: HTMLCanvasElement, maxBytes = MY_STICKER_MAX_BYTES): Promise<Blob> {
  const png = await toBlob(canvas, "image/png");
  if (png && png.size <= maxBytes) return png;
  for (const q of [0.92, 0.82, 0.7]) {
    const webp = await toBlob(canvas, "image/webp", q);
    // Browsers without a WebP encoder silently return PNG: check the type.
    if (webp && webp.type === "image/webp" && webp.size <= maxBytes) return webp;
  }
  for (const side of [448, 384, 320, 256]) {
    if (side >= Math.max(canvas.width, canvas.height)) continue;
    const small = await toBlob(scaledCopy(canvas, side), "image/png");
    if (small && small.size <= maxBytes) return small;
  }
  throw new Error("That sticker is too detailed to keep — try the Circle shape or erase more.");
}

/**
 * Re-encode a picture shown from a blob: URL (a received / sent sticker) —
 * through <img>, because CSP `connect-src` does not allow fetch(blob:). The
 * round trip through canvas also strips anything but pixels.
 */
export async function stickerBlobFromImageUrl(url: string, longSide = 512): Promise<Blob> {
  let img: HTMLImageElement;
  try {
    img = await decodeWithImg(url);
  } catch {
    throw new Error("Couldn't read that sticker.");
  }
  const w0 = img.naturalWidth;
  const h0 = img.naturalHeight;
  if (!w0 || !h0 || w0 * h0 > MAX_SOURCE_PIXELS) throw new Error("Couldn't read that sticker.");
  const k = Math.min(1, longSide / Math.max(w0, h0));
  const { canvas, ctx } = canvas2d(Math.max(1, Math.round(w0 * k)), Math.max(1, Math.round(h0 * k)));
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return encodeSticker(canvas);
}

/** Raster type from the magic bytes — never trust a declared MIME (no SVG, ever). */
export function sniffStickerType(bytes: Uint8Array): MyStickerMediaType | null {
  const b = (i: number) => bytes[i] ?? -1;
  if (b(0) === 0x89 && b(1) === 0x50 && b(2) === 0x4e && b(3) === 0x47 && b(4) === 0x0d && b(5) === 0x0a) {
    return "image/png";
  }
  if (b(0) === 0x47 && b(1) === 0x49 && b(2) === 0x46 && b(3) === 0x38 && (b(4) === 0x37 || b(4) === 0x39)) {
    return "image/gif";
  }
  if (
    b(0) === 0x52 && b(1) === 0x49 && b(2) === 0x46 && b(3) === 0x46 &&
    b(8) === 0x57 && b(9) === 0x45 && b(10) === 0x42 && b(11) === 0x50
  ) {
    return "image/webp";
  }
  if (b(0) === 0xff && b(1) === 0xd8 && b(2) === 0xff) return "image/jpeg";
  return null;
}

export function stickerExtension(type: MyStickerMediaType): string {
  return type === "image/jpeg" ? "jpg" : type.slice("image/".length);
}
